import { Alert, Box, Button, Typography } from '@mui/material';
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, RouterProvider, useRouteError } from 'react-router';

import { LoadingState } from './components/states.js';
import AppShell from './layout/app-shell.js';

const LoginPage = lazy(() => import('./auth/login-page.js'));
const DemoEntryPage = lazy(() => import('./auth/demo-entry-page.js'));
const DashboardPage = lazy(() => import('./features/dashboard/dashboard-page.js'));
const DocumentsPage = lazy(() => import('./features/documents/documents-page.js'));
const ReportsPage = lazy(() => import('./features/reports/reports-page.js'));
const NotificationsPage = lazy(() => import('./features/notifications/notifications-page.js'));
const MembersPage = lazy(() => import('./features/admin/members-page.js'));
const SecurityPage = lazy(() => import('./features/admin/security-page.js'));
const IntegrationsPage = lazy(() => import('./features/admin/integrations-page.js'));
const OrganizationPage = lazy(() => import('./features/admin/organization-page.js'));
const CompanyDetailPage = lazy(() => import('./features/companies/company-detail-page.js'));
const MyWorkPage = lazy(() => import('./features/work/my-work-page.js'));
const CalendarPage = lazy(() => import('./features/calendar/calendar-page.js'));
const JobFilesPage = lazy(() => import('./features/job-files/job-files-page.js'));
const JobFilePage = lazy(() => import('./features/job-files/job-file-page.js'));
const IntakePage = lazy(() => import('./features/intake/intake-page.js'));
const LeadPage = lazy(() => import('./features/leads/lead-page.js'));
const InspectionPage = lazy(() => import('./features/inspections/inspection-page.js'));
const ProjectPage = lazy(() => import('./features/projects/project-page.js'));
const MilestonePage = lazy(() => import('./features/workflows/milestone-page.js'));
const EquipmentPage = lazy(() => import('./features/equipment/equipment-page.js'));
const CatalogsPage = lazy(() => import('./features/admin/catalogs-page.js'));
const WorkflowTemplatesPage = lazy(() => import('./features/admin/workflow-templates-page.js'));
const CompaniesPage = lazy(() =>
  import('./features/resources/resource-pages.js').then((module) => ({
    default: module.CompaniesPage,
  })),
);
const ContactsPage = lazy(() =>
  import('./features/resources/resource-pages.js').then((module) => ({
    default: module.ContactsPage,
  })),
);
const LeadsPage = lazy(() =>
  import('./features/resources/resource-pages.js').then((module) => ({
    default: module.LeadsPage,
  })),
);
const OpportunitiesPage = lazy(() =>
  import('./features/resources/resource-pages.js').then((module) => ({
    default: module.OpportunitiesPage,
  })),
);
const ProjectsPage = lazy(() =>
  import('./features/resources/resource-pages.js').then((module) => ({
    default: module.ProjectsPage,
  })),
);
const TasksPage = lazy(() =>
  import('./features/resources/resource-pages.js').then((module) => ({
    default: module.TasksPage,
  })),
);

function withSuspense(component: React.ReactNode): React.JSX.Element {
  return <Suspense fallback={<LoadingState label="Loading page" />}>{component}</Suspense>;
}

function RouteError(): React.JSX.Element {
  const error = useRouteError();
  return (
    <Box component="main" sx={{ p: 4 }}>
      <Typography component="h1" variant="h1" sx={{ mb: 2 }}>
        This page could not be displayed
      </Typography>
      <Alert severity="error">
        {error instanceof Error ? error.message : 'An unexpected routing error occurred.'}
      </Alert>
      <Button href="/" sx={{ mt: 2 }}>
        Return to dashboard
      </Button>
    </Box>
  );
}

const router = createBrowserRouter([
  { path: '/login', element: withSuspense(<LoginPage />), errorElement: <RouteError /> },
  { path: '/demo/:key', element: withSuspense(<DemoEntryPage />), errorElement: <RouteError /> },
  {
    path: '/',
    element: <AppShell />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: withSuspense(<DashboardPage />) },
      { path: 'companies', element: withSuspense(<CompaniesPage />) },
      { path: 'companies/:id', element: withSuspense(<CompanyDetailPage />) },
      { path: 'my-work', element: withSuspense(<MyWorkPage />) },
      { path: 'calendar', element: withSuspense(<CalendarPage />) },
      { path: 'job-files', element: withSuspense(<JobFilesPage />) },
      { path: 'job-files/:id', element: withSuspense(<JobFilePage />) },
      { path: 'intake', element: withSuspense(<IntakePage />) },
      { path: 'leads/:id', element: withSuspense(<LeadPage />) },
      { path: 'inspections/:id', element: withSuspense(<InspectionPage />) },
      { path: 'projects/:id', element: withSuspense(<ProjectPage />) },
      { path: 'milestones/:id', element: withSuspense(<MilestonePage />) },
      { path: 'equipment', element: withSuspense(<EquipmentPage />) },
      { path: 'admin/catalogs', element: withSuspense(<CatalogsPage />) },
      { path: 'admin/workflow-templates', element: withSuspense(<WorkflowTemplatesPage />) },
      { path: 'contacts', element: withSuspense(<ContactsPage />) },
      { path: 'leads', element: withSuspense(<LeadsPage />) },
      { path: 'opportunities', element: withSuspense(<OpportunitiesPage />) },
      { path: 'projects', element: withSuspense(<ProjectsPage />) },
      { path: 'tasks', element: withSuspense(<TasksPage />) },
      { path: 'documents', element: withSuspense(<DocumentsPage />) },
      { path: 'reports', element: withSuspense(<ReportsPage />) },
      { path: 'notifications', element: withSuspense(<NotificationsPage />) },
      { path: 'admin/members', element: withSuspense(<MembersPage />) },
      { path: 'admin/security', element: withSuspense(<SecurityPage />) },
      { path: 'admin/integrations', element: withSuspense(<IntegrationsPage />) },
      { path: 'admin/organization', element: withSuspense(<OrganizationPage />) },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);

export function App(): React.JSX.Element {
  return <RouterProvider router={router} />;
}
